import GaiaIcon from '@gaia/icons/GaiaIcon';
import {useUser} from '@gaia/stores/user';
import theme from '@gaia/theme';
import gettext from '@gaia/utils/django/gettext';
import fetchJSON from '@gaia/utils/fetchJSON';
import {Badge, CircularProgress, Menu, MenuItem} from '@mui/material';
import {
    type ElementType,
    type HTMLProps,
    type RefObject,
    type SyntheticEvent,
    useEffect,
    useRef,
    useState,
} from 'react';

import {MenuItemAnchor} from './MenuItemAnchor';

interface ToolbarProps extends HTMLProps<HTMLAnchorElement> {
    component?: ElementType;
    ref?: RefObject<any>;
}

interface Notification {
    isViewed: boolean;
    html: string;
    id: number;
}

function Toolbar(props: ToolbarProps) {
    const {component, style, children} = props;
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const ref = useRef<HTMLDivElement>(null);

    const {isAuthenticated} = useUser();

    useEffect(() => {
        async function fetchNotifications() {
            if (!isAuthenticated) return [];
            setIsLoading(true);

            try {
                const ns = await fetchJSON('/social/notifications/popup/');
                setNotifications(ns);
                setIsLoading(false);
            } catch (e) {
                console.error(e);
            }
        }

        fetchNotifications().catch(console.warn);
    }, [isAuthenticated]);

    if (!isAuthenticated) return null;

    return (
        <>
            {renderButton()}
            {renderMenu()}
        </>
    );

    // @TODO: nested components are an anti-pattern

    function renderButton() {
        const Component = component || 'button';

        return (
            <Component
                {...props}
                aria-label='Notifications'
                onClick={(event: SyntheticEvent<HTMLButtonElement>) => {
                    event.preventDefault();
                    setOpen(true);
                    markNotificationsViewed();
                }}
                style={{
                    ...style,
                    background: 'none',
                    border: 0,
                    paddingRight: theme.spacing(1),
                }}
            >
                <div ref={ref}>{children || renderButtonIcon()}</div>
            </Component>
        );
    }

    function renderButtonIcon() {
        if (isLoading) return <GaiaIcon name='alert' />;

        const unviewedNotifications = notifications.filter(({isViewed}) => !isViewed);

        if (unviewedNotifications.length) {
            return (
                <Badge badgeContent={unviewedNotifications.length} color='error'>
                    <GaiaIcon name='alert' />
                </Badge>
            );
        } else {
            return <GaiaIcon name='alert' />;
        }
    }

    function renderMenu() {
        return (
            <Menu anchorEl={ref.current} open={open} onClose={() => setOpen(false)}>
                {renderMenuInner()}
            </Menu>
        );
    }

    function renderMenuInner() {
        if (isLoading) {
            return <CircularProgress style={{margin: theme.spacing(2)}} />;
        } else if (notifications.length === 0) {
            return <MenuItem>You have no notifications</MenuItem>;
        } else {
            return notifications.map((notification) => (
                <MenuItem
                    key={notification.id}
                    style={{
                        height: 'auto',
                        maxWidth: theme.spacing(30),
                        whiteSpace: 'normal',
                    }}
                >
                    {
                        <div
                            dangerouslySetInnerHTML={{
                                __html: notification.html,
                            }}
                        />
                    }
                </MenuItem>
            ));
        }
    }

    function markNotificationsViewed() {
        Promise.all(
            notifications.map((notification) =>
                fetch(`/social/notifications/${notification.id}/markviewed/`, {
                    method: 'post',
                    credentials: 'same-origin',
                })
            )
        )
            .then(() =>
                setNotifications(
                    notifications.map((notification) => ({
                        ...notification,
                        isViewed: true,
                    }))
                )
            )
            .catch(console.error);
    }
}

function HamburgerMenu(props: ToolbarProps) {
    if (!useUser().isAuthenticated) return null;

    return (
        <Toolbar component={MenuItemAnchor} {...props}>
            {gettext('Notifications')}
        </Toolbar>
    );
}

export const HeaderNotifications = {Toolbar, HamburgerMenu};
